import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  ErrorBanner,
  PrimaryButton,
  SearchBox,
  SkeletonText,
} from '@/components';
import { FormSection } from '@/components/FormSection';
import { cn } from '@/lib';
import { useEmployeeList } from '@/features/employees/hooks';
import type { Employee } from '@/features/employees/types';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { contactDisplayName } from '../api';
import type { FieldSalesRepresentativeContact } from '../api';
import {
  useAddFieldSalesRepresentative,
  useFieldSalesRepresentatives,
  useRemoveFieldSalesRepresentative,
} from '../hooks';

interface FieldSalesRepresentativeModifySectionProps {
  companyId: string;
  customerCompanyId: string;
  companyName?: string;
}

/**
 * @component FieldSalesRepresentativeModifySection
 * @description Lets an authorized user assign, clear, or reselect the active company's field sales representative for a customer company.
 *
 * @props
 *   companyId (string, required) - Active company ID that owns the representative contact.
 *   customerCompanyId (string, required) - Customer company ID receiving the representative assignment.
 *   companyName (string, optional) - Active company name shown in the section title.
 *
 * @changelog
 *   2026-07-08 - Initial creation.
 */
export function FieldSalesRepresentativeModifySection({
  companyId,
  customerCompanyId,
  companyName,
}: FieldSalesRepresentativeModifySectionProps) {
  const { t } = useTranslation();
  const [selectedContactId, setSelectedContactId] = useState<string | null>(
    null
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const {
    data: currentRepresentatives = [],
    isLoading: isLoadingAssignment,
    isError: isAssignmentError,
  } = useFieldSalesRepresentatives(companyId, customerCompanyId);
  const {
    employees,
    isLoading: isLoadingContacts,
    searchInput,
    setSearchInput,
  } = useEmployeeList({
    companyId,
    initialPageSize: 50,
    // Only load selectable contacts once the picker is actually opened.
    enabled: isSelectorOpen,
  });
  const addRepresentative = useAddFieldSalesRepresentative(companyId);
  const removeRepresentative = useRemoveFieldSalesRepresentative(companyId);

  const currentRepresentative = currentRepresentatives[0];
  const currentContactId = currentRepresentative?.id ?? null;
  const contactEmployees = employees.filter((employee) =>
    Boolean(employee.contact?.id)
  );

  useEffect(() => {
    setSelectedContactId(currentContactId);
  }, [currentContactId]);

  const handleOpenSelector = () => {
    setSubmitError(null);
    setSelectedContactId(currentContactId);
    setIsSelectorOpen(true);
  };

  const handleCloseSelector = () => {
    setSelectedContactId(currentContactId);
    setIsSelectorOpen(false);
  };

  const handleSave = async () => {
    if (!selectedContactId) return;

    setSubmitError(null);
    try {
      await addRepresentative.mutateAsync({
        customerCompanyId,
        companyContactId: selectedContactId,
      });
      setIsSelectorOpen(false);
    } catch {
      setSubmitError(t('companyAdmin.form.fieldSalesRepresentative.saveError'));
    }
  };

  const handleClear = async () => {
    setSubmitError(null);
    try {
      await removeRepresentative.mutateAsync(customerCompanyId);
      setSelectedContactId(null);
      setIsSelectorOpen(false);
    } catch {
      setSubmitError(
        t('companyAdmin.form.fieldSalesRepresentative.clearError')
      );
    }
  };

  const sectionTitle = companyName
    ? t('companyAdmin.form.fieldSalesRepresentative.titleFromCompany', {
      company: companyName,
    })
    : t('companyAdmin.form.fieldSalesRepresentative.title');
  const isSaving = addRepresentative.isPending;
  const isClearing = removeRepresentative.isPending;
  const canSave = Boolean(
    selectedContactId && selectedContactId !== currentContactId
  );
  const hasCurrentRepresentative = Boolean(currentContactId);

  return (
    <FormSection icon="contact" title={sectionTitle}>
      <ErrorBanner hasError={isAssignmentError}>
        {t('companyAdmin.form.fieldSalesRepresentative.loadError')}
      </ErrorBanner>
      <ErrorBanner hasError={Boolean(submitError)}>{submitError}</ErrorBanner>
      <CurrentRepresentative
        isLoading={isLoadingAssignment}
        representative={currentRepresentative}
        onModify={handleOpenSelector}
      />
      <RepresentativeSelectorDialog
        isOpen={isSelectorOpen}
        employees={contactEmployees}
        isLoading={isLoadingContacts}
        searchInput={searchInput}
        selectedContactId={selectedContactId}
        canSave={canSave}
        isClearing={isClearing}
        isSaving={isSaving}
        hasCurrentRepresentative={hasCurrentRepresentative}
        onClose={handleCloseSelector}
        onSearchChange={setSearchInput}
        onSelect={setSelectedContactId}
        onClear={handleClear}
        onSave={handleSave}
      />
    </FormSection>
  );
}

function CurrentRepresentative({
  isLoading,
  representative,
  onModify,
}: CurrentRepresentativeProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      // Loading block for the currently saved representative.
      <div className="border-border bg-surface rounded-lg border p-3">
        <SkeletonText lines={2} />
      </div>
    );
  }

  if (!representative) {
    return (
      <RepresentativeSummaryButton onModify={onModify} hasValue={false}>
        <p className="text-muted text-sm">
          {t(
            'companyAdmin.form.fieldSalesRepresentative.noCurrentRepresentative'
          )}
        </p>
      </RepresentativeSummaryButton>
    );
  }

  return (
    <RepresentativeSummaryButton onModify={onModify} hasValue={true}>
      <p className="text-muted text-xs">
        {t('companyAdmin.form.fieldSalesRepresentative.currentRepresentative')}
      </p>
      <p className="text-onsurface text-sm font-medium">
        {contactDisplayName(representative)}
      </p>
      <p className="text-muted text-xs">{representative.email ?? '-'}</p>
    </RepresentativeSummaryButton>
  );
}

function RepresentativeSummaryButton({
  children,
  onModify,
  hasValue
}: RepresentativeSummaryButtonProps) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onModify}
      className="border-border bg-surface hover:bg-surface-darker flex w-full items-start justify-between gap-4 rounded-lg border p-3 text-left transition-colors"
    >
      <span className="min-w-0 flex-1">{children}</span>
      <span className="text-primary shrink-0 text-sm font-medium">
        {hasValue
          ? t('companyAdmin.form.fieldSalesRepresentative.change')
          : t('companyAdmin.form.fieldSalesRepresentative.add')}
      </span>
    </button>
  );
}

function RepresentativeSelectorDialog({
  isOpen,
  employees,
  isLoading,
  searchInput,
  selectedContactId,
  canSave,
  isClearing,
  isSaving,
  hasCurrentRepresentative,
  onClose,
  onSearchChange,
  onSelect,
  onClear,
  onSave,
}: RepresentativeSelectorDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog isOpen={isOpen} onClose={onClose} className="max-h-[90vh] max-w-lg">
      <DialogHeader showClose>
        {t('companyAdmin.form.fieldSalesRepresentative.selectTitle')}
      </DialogHeader>
      <DialogBody className="flex max-h-[65vh] flex-col gap-3 overflow-y-auto">
        <ContactSelector
          employees={employees}
          isLoading={isLoading}
          searchInput={searchInput}
          selectedContactId={selectedContactId}
          onSearchChange={onSearchChange}
          onSelect={onSelect}
          onSelectConfirm={(idx) => {
            onSelect(idx)
            onSave()
          }}
        />
      </DialogBody>
      <DialogFooter>
        <FieldSalesRepresentativeActions
          canSave={canSave}
          isClearing={isClearing}
          isSaving={isSaving}
          hasCurrentRepresentative={hasCurrentRepresentative}
          onClear={onClear}
          onSave={onSave}
        />
      </DialogFooter>
    </Dialog>
  );
}

function ContactSelector({
  employees,
  isLoading,
  searchInput,
  selectedContactId,
  onSearchChange,
  onSelect,
  onSelectConfirm,
}: ContactSelectorProps) {
  const { t } = useTranslation();

  return (
    // Search and selectable contact list for the active company.
    <div className="flex flex-col gap-3">
      <SearchBox
        value={searchInput}
        onChange={onSearchChange}
        placeholder={t(
          'companyAdmin.form.fieldSalesRepresentative.searchPlaceholder'
        )}
        ariaLabel={t('companyAdmin.form.fieldSalesRepresentative.searchLabel')}
        clearAriaLabel={t('employees.search.clear')}
      />
      <ContactSelectorContent
        employees={employees}
        isLoading={isLoading}
        selectedContactId={selectedContactId}
        onSelect={onSelect}
        onSelectConfirm={onSelectConfirm}
      />
    </div>
  );
}

function ContactSelectorContent({
  employees,
  isLoading,
  selectedContactId,
  onSelect,
  onSelectConfirm,
}: ContactSelectorContentProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      // Loading block for selectable contact rows.
      <div className="border-border bg-surface rounded-lg border p-3">
        <SkeletonText lines={3} />
      </div>
    );
  }

  if (employees.length === 0) {
    return (
      // Empty state when the active company has no selectable contacts.
      <p className="text-muted text-sm">
        {t('companyAdmin.form.fieldSalesRepresentative.noContacts')}
      </p>
    );
  }

  return (
    // Scrollable list of contacts that can become the representative.
    <div className="grid max-h-[300px] grid-cols-1 gap-2 overflow-y-auto p-1">
      {employees.map((employee) => (
        <ContactChoice
          key={employee.id}
          employee={employee}
          isSelected={employee.contact?.id === selectedContactId}
          onSelect={onSelect}
          onSelectConfirm={onSelectConfirm}
        />
      ))}
    </div>
  );
}

function ContactChoice({ employee, isSelected, onSelect, onSelectConfirm }: ContactChoiceProps) {
  const contact = employee.contact;

  if (!contact) {
    return null;
  }

  const handleSelect = () => {
    onSelect(contact.id)
  };

  return (
    <button
      type="button"
      onClick={handleSelect}
      onDoubleClick={() => onSelectConfirm(contact.id)}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-all',
        isSelected && 'border-primary bg-primary/5 ring-primary ring-1',
        !isSelected &&
        'border-border bg-surface hover:border-primary/50 hover:bg-surface-darker'
      )}
    >
      <span
        className={cn(
          'flex size-4 shrink-0 items-center justify-center rounded-full border-2',
          isSelected && 'border-primary',
          !isSelected && 'border-muted'
        )}
      >
        {isSelected && <span className="bg-primary size-2 rounded-full" />}
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block truncate text-sm font-medium',
            isSelected && 'text-primary',
            !isSelected && 'text-onsurface'
          )}
        >
          {contact.name}
        </span>
        <span className="text-muted block truncate text-xs">
          {contact.email || employee.user?.userEmail || '-'}
        </span>
      </span>
    </button>
  );
}

function FieldSalesRepresentativeActions({
  canSave,
  isClearing,
  isSaving,
  hasCurrentRepresentative,
  onClear,
  onSave,
}: FieldSalesRepresentativeActionsProps) {
  const { t } = useTranslation();

  return (
    // Action row for persisting or clearing the assignment.
    <div className="flex flex-wrap justify-end gap-2">
      <Button
        type="button"
        disabled={!hasCurrentRepresentative || isClearing || isSaving}
        onClick={onClear}
      >
        {isClearing
          ? t('common.updating')
          : t('companyAdmin.form.fieldSalesRepresentative.clear')}
      </Button>
      <PrimaryButton
        type="button"
        disabled={!canSave || isClearing}
        isLoading={isSaving}
        onClick={onSave}
      >
        {hasCurrentRepresentative
          ? t('companyAdmin.form.fieldSalesRepresentative.reselect')
          : t('companyAdmin.form.fieldSalesRepresentative.add')}
      </PrimaryButton>
    </div>
  );
}

interface CurrentRepresentativeProps {
  isLoading: boolean;
  representative?: FieldSalesRepresentativeContact;
  onModify: () => void;
}

interface RepresentativeSummaryButtonProps {
  children: ReactNode;
  hasValue: boolean;
  onModify: () => void;
}

interface RepresentativeSelectorDialogProps {
  isOpen: boolean;
  employees: Employee[];
  isLoading: boolean;
  searchInput: string;
  selectedContactId: string | null;
  canSave: boolean;
  isClearing: boolean;
  isSaving: boolean;
  hasCurrentRepresentative: boolean;
  onClose: () => void;
  onSearchChange: (value: string) => void;
  onSelect: (contactId: string) => void;
  onClear: () => void;
  onSave: () => void;
}

interface ContactSelectorProps {
  employees: Employee[];
  isLoading: boolean;
  searchInput: string;
  selectedContactId: string | null;
  onSearchChange: (value: string) => void;
  onSelect: (contactId: string) => void;
  onSelectConfirm: (contactId: string) => void;
}

interface ContactSelectorContentProps {
  employees: Employee[];
  isLoading: boolean;
  selectedContactId: string | null;
  onSelect: (contactId: string) => void;
  onSelectConfirm: (contactId: string) => void;
}

interface ContactChoiceProps {
  employee: Employee;
  isSelected: boolean;
  onSelect: (contactId: string) => void;
  onSelectConfirm: (contactId: string) => void;
}

interface FieldSalesRepresentativeActionsProps {
  canSave: boolean;
  isClearing: boolean;
  isSaving: boolean;
  hasCurrentRepresentative: boolean;
  onClear: () => void;
  onSave: () => void;
}
